/**
 * SignInLayer — the sign-in gate as one layer: the scrim, and the shared `SignInCard` centred on
 * it. "Sign-in is the one overlay (the shipped card); one layer at a time" (BRIEF §12). It was
 * Toile's (`products/toile/components/SignInLayer/`) and moved here with the card, plan
 * `2026_09_17_ovid_one_wall.md` § Decided 6 — three surfaces open it now (Toile's wall, Toile's
 * pack page, Ovid's wall), and a gate that looked or behaved differently on any of them would be a
 * second vocabulary for one act.
 *
 * It owns the scrim's dismiss and the `auth-error` read, so a caller supplies only where to come
 * back to, what raised the gate, and — a house with its own card surface — the card's own props.
 *
 * `redirectTo` is the caller's own return URL, carrying whatever the visitor had asked for: the
 * wall's chain and words (`chains.returnTokenFor`), the page's press (`pageVerb.packReturnToken`),
 * or a closed house's own token (`HouseTheme.visitor.returnTo`). It reaches the card as a form
 * field, which is the only way it survives the OIDC round-trip (`SignInCard`'s own note).
 */
import type {ComponentProps, ReactNode} from 'react'
import {useSearchParams} from 'react-router'

import {routerPaths} from '@routes'
import {SignInCard} from '@core/auth-ui/SignInCard/SignInCard'
import defaultStyles from './SignInLayer.module.scss'

type SignInLayerProps = {
    redirectTo: string
    /** `GATE_TRIGGER_WALL` / `GATE_TRIGGER_PAGE_VERB` (`@core/wall/contract`) — what raised the
     *  gate, carried onto `sign_in`/`sign_up` on the far side. */
    trigger: string
    onClose: () => void
    /** The house's own card surface — glyph, the two lines, its terms/privacy hrefs, a stylesheet.
     *  Everything the card does is the same in every house; only these travel. */
    card?: Pick<ComponentProps<typeof SignInCard>, 'glyph' | 'copy' | 'termsHref' | 'privacyHref' | 'styles' | 'inputId'>
    /** The layer's own stylesheet (scrim, centring, stacking), when a house's surface asks for one. */
    styles?: Record<string, string>
    /** Rendered inside the layer, above the card — a house's own close button, say. */
    children?: ReactNode
}

export function SignInLayer({redirectTo, trigger, onClose, card, styles = defaultStyles, children}: SignInLayerProps) {
    const [searchParams] = useSearchParams()
    return (
        // Error-handling review F11: the wall's return listener stays attached while the sign-in
        // card is up (`fire()` sets `signInOpen` and never touches `overlayOpen`), so a wheel or
        // drag over this layer past the return threshold used to fold the column/sheet beneath it —
        // the sign-in stays open (its own scrim-click/Escape are what actually dismiss it), but the
        // visitor's armed chain and words were lost underneath. Marked exempt like the layer's own
        // interactive content elsewhere (`Wall.tsx`'s `[data-wall-return-exempt]`).
        <div className={styles.overlay} data-wall-return-exempt="true">
            <div className={styles.scrim} onClick={onClose} />
            <div className={styles.card}>
                {children}
                <SignInCard
                    {...card}
                    googleAuthAction={routerPaths['/auth/:provider']({provider: 'google'})}
                    authError={searchParams.get('auth-error')}
                    redirectTo={redirectTo}
                    gateTrigger={trigger}
                />
            </div>
        </div>
    )
}
